import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import AIContentAction from "../actions/AIContentAction";
import TopicsModal from "./TopicsModal";
import PostDetailModal from "./PostDetailModal";
import PromptHeader from "./components/PromptHeader";
import GenerateModal from "./GenerateModal";
import CalendarView from "./components/CalendarView";
import PostsList from "./components/PostsList";
import PostCard from "./components/PostCard";
import ConfirmDialog from "../dialog/ConfirmDialog";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import LayoutAction from "../actions/LayoutAction";
import TaskAction from "../actions/TaskAction";
import { deleteTask } from "../api/ProjectApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  generateSocialPosts as apiGenerateSocialPosts,
  searchSocialPosts as apiSearchSocialPosts,
  unscheduleAllSocialPosts as apiUnscheduleAllSocialPosts,
} from "../api/AIContentApi";
import {
  selectAiSetting,
  selectHasFullAiSetting,
  selectSocialPosts,
  getAllowedPlatformsFromImmutable,
  isPostOnAllowedPlatform,
} from "./selectors/aiContentSelectors";
import { resolveContentInstructions } from "./constants/contentInstructionsDefaults";
import {
  formatISO,
  startOfDay,
  format,
  subMilliseconds,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import CalendarToolbar from "./components/CalendarToolbar";
import MediaGrid from "./components/MediaGrid";
import SharePostModal from "./SharePostModal";
import BillingAction from "../actions/BillingAction";
import {
  getAiContentQuotaErrorMessage,
  handleAiContentQuotaError,
} from "./utils/aiContentQuotaUtils";

interface AIContentAppProps {
  initialView?: string;
}

// Compute month grid visible range using date-fns (includes extra days outside the month)
const computeMonthGridRange = (baseDate: Date) => {
  const monthStart = startOfMonth(baseDate);
  const monthEnd = endOfMonth(baseDate);
  const visibleStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const visibleEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  return { visibleStart, visibleEnd };
};

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

const toValidDate = (value: unknown): Date | undefined => {
  if (isValidDate(value)) return value;
  if (value == null) return undefined;
  const parsed = new Date(value as any);
  return isValidDate(parsed) ? parsed : undefined;
};

/** refreshTrigger may be a plain object (merge) or Immutable Map (after toJS/fromJS). */
const readRefreshTriggerRange = (
  refreshTrigger: any
): { start?: Date; end?: Date } => {
  if (!refreshTrigger) return {};
  return {
    start: toValidDate(refreshTrigger.start ?? refreshTrigger.get?.("start")),
    end: toValidDate(refreshTrigger.end ?? refreshTrigger.get?.("end")),
  };
};

const AIContentApp: React.FC<AIContentAppProps> = ({ initialView = "dayGridMonth" }) => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const socialPosts = useSelector(selectSocialPosts);
  const fileImages = useSelector((state: any) => state.aiContent?.get?.("fileImages")) || fromJS([]);
  const canvaDesigns = useSelector((state: any) => state.aiContent?.get?.("canvaDesigns")) || fromJS([]);
  const currentDateFromRedux = useSelector(
    (state: any) => state.aiContent?.get?.("currentDate") || null
  );
  const refreshTrigger = useSelector((state: any) => state.aiContent?.get?.("refreshTrigger"));
  const settingsChangedSinceLastGenerate = useSelector(
    (state: any) => state.aiContent?.get?.("settingsChangedSinceLastGenerate") === true
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const viewMode = (searchParams.get("contentView") as "calendar" | "media") || "calendar";

  const prevViewMode = useRef<"calendar" | "media">("calendar");
  useEffect(() => {
    if (viewMode === "media" && prevViewMode.current !== "media" && projectId) {
      dispatch(AIContentAction.getFileImages(projectId) as any);
      dispatch(AIContentAction.getCanvaDesigns() as any);
    }
    prevViewMode.current = viewMode;
  }, [viewMode, projectId, dispatch]);

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeletePostConfirm, setShowDeletePostConfirm] = useState(false);
  const [postIdToDelete, setPostIdToDelete] = useState<string | null>(null);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [showClearDraftConfirm, setShowClearDraftConfirm] = useState(false);
  const [calendarViewHeight, setCalendarViewHeight] = useState(0);
  const [topic, setTopic] = useState("");
  const calendarRef = useRef<any>(null);
  const [isFullCalendarView, setIsFullCalendarView] = useState(false);
  const [monthAnimationKey, setMonthAnimationKey] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showGenerateErrorConfirm, setShowGenerateErrorConfirm] = useState(false);
  const [generateErrorMessage, setGenerateErrorMessage] = useState<string>("");
  const firstRangeSeenRef = useRef(false);

  // Use Redux selectors
  const aiSetting = useSelector(selectAiSetting);
  const hasValidAiSetting = useSelector(selectHasFullAiSetting);

  useEffect(() => {
    document.title = "Socials";
  }, []);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
    dispatch(BillingAction.getBillingUserLimits() as any);
  }, [projectId, dispatch]);

  useEffect(() => {
    const defaultTopic = aiSetting?.get?.("topic") || "";
    if (defaultTopic && !topic) {
      setTopic(defaultTopic);
    }
  }, [aiSetting, topic]);

  const baseForFallback = (() => {
    if (!currentDateFromRedux) return new Date();
    const parsed = new Date(currentDateFromRedux as any);
    return isValidDate(parsed) ? parsed : new Date();
  })();
  const { visibleStart: fallbackStart, visibleEnd: fallbackEnd } =
    computeMonthGridRange(baseForFallback);

  // Prefer the calendar's last visible range. After dialog open, refreshTrigger
  // can become an Immutable Map — reading only `.start` fell back to the current
  // month grid and wiped next-month posts from the query.
  const refreshRange = readRefreshTriggerRange(refreshTrigger);
  const triggerStart = refreshRange.start ?? fallbackStart;
  const triggerEnd = refreshRange.end ?? fallbackEnd;
  const rangeStart = startOfDay(triggerStart);
  const rangeEnd = subMilliseconds(new Date(triggerEnd), 1);

  useEffect(() => {
    if (rangeStart && rangeEnd) return;
    if (firstRangeSeenRef.current) return;
    const base = currentDateFromRedux ? new Date(currentDateFromRedux) : new Date();
    const { visibleStart, visibleEnd } = computeMonthGridRange(base);
    dispatch(
      AIContentAction.refreshCalendarData({
        start: visibleStart,
        end: visibleEnd,
      }) as any
    );
    firstRangeSeenRef.current = true;
  }, [rangeStart, rangeEnd, currentDateFromRedux, dispatch]);

  const filters =
    rangeStart && rangeEnd
      ? {
        start: {
          operator: "dateRange",
          values: [formatISO(rangeStart), formatISO(rangeEnd)],
        },
      }
      : {};

  const socialPostsQuery = useQuery({
    queryKey: [
      "socialPosts",
      projectId,
      rangeStart ? formatISO(rangeStart) : null,
      rangeEnd ? formatISO(rangeEnd as Date) : null,
    ],
    enabled: Boolean(hasValidAiSetting && projectId),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    queryFn: async () => {
      return await apiSearchSocialPosts({
        projectId: projectId!,
        page: 0,
        pageSize: 500,
        filters,
        fields: [],
        sorts: [],
      });
    },
    onError: (error: any) => {
      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "error",
          message: t("Error generating posts: {{error}}", {
            error: String(error?.message || error),
          }),
        }) as any
      );
      setInitialLoading(false);
    },
  } as any);

  const queryData = (socialPostsQuery as any).data;
  useEffect(() => {
    if (!queryData) return;
    const posts: any[] = Array.isArray(queryData?.pageResult?.content)
      ? queryData.pageResult.content
      : [];
    dispatch(AIContentAction.setSocialPosts(posts) as any);
    if (firstRangeSeenRef.current) {
      setInitialLoading(false);
    }
  }, [queryData, dispatch]);

  const lastGeneratedStartDateRef = useRef<Date | null>(null);
  const lastGenerateParamsRef = useRef<any>(null);

  const generatePostsMutation = useMutation({
    onMutate: async () => {
      dispatch(
        LayoutAction.setNotification({
          title: t("Our AI is generating new posts for you"),
          type: "success",
          variant: "aiGenerating",
          message: t(
            "Preparing pieces of unique content. This may take up to 2 minutes, click refresh if needed."
          ),
          duration: 120000,
        }) as any
      );
    },
    mutationFn: async (params: any) => {
      return await apiGenerateSocialPosts(params.projectId, params, params.override);
    },
    onError: (error: any) => {
      if (handleAiContentQuotaError(dispatch, error)) {
        dispatch(BillingAction.getBillingTokenUsage(projectId!) as any);
        return;
      }
      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "error",
          message: t("Error generating posts: {{error}}", {
            error: String(error?.message || error),
          }),
        }) as any
      );
    },
    onSuccess: (data: any) => {
      if (projectId) {
        dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
      }
      const apiError =
        (data && (data.error || data.message)) ||
        (data?.data && (data.data.error || data.data.message));
      if (apiError) {
        setGenerateErrorMessage(String(apiError));
        setShowGenerateErrorConfirm(true);
        return;
      }
      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "success",
          message: t("Posts generated successfully!"),
        }) as any
      );

      // Navigate calendar to the selected start month
      try {
        const targetDate = lastGeneratedStartDateRef.current;
        const api = calendarRef.current?.getApi?.();
        if (targetDate && api) {
          // Ensure month view is visible at the selected month
          if (typeof api.changeView === "function") {
            api.changeView("dayGridMonth", targetDate);
          } else if (typeof api.gotoDate === "function") {
            api.gotoDate(targetDate);
          }
        }
      } catch { }

      // Trigger month animation explicitly (only on generate success and when month actually changes)
      try {
        const target = lastGeneratedStartDateRef.current;
        if (target) {
          const current = currentDateFromRedux ? new Date(currentDateFromRedux) : new Date();
          const currentKey = `${current.getFullYear()}-${current.getMonth()}`;
          const targetKey = `${target.getFullYear()}-${target.getMonth()}`;
          if (currentKey !== targetKey) {
            setMonthAnimationKey((k) => k + 1);
          }
        }
      } catch { }

      // Refetch social posts after successful generation
      socialPostsQuery.refetch();
    },
    onSettled: () => { },
  });

  const triggerGenerateSocialPosts = useCallback(
    (params: any) => {
      if (generatePostsMutation.isPending) return;
      generatePostsMutation.mutate(params);
    },
    [generatePostsMutation]
  );

  const handleCalendarHeightChange = useCallback(
    (height: number) => {
      if (height !== calendarViewHeight && height > 0) {
        setCalendarViewHeight(height);
      }
    },
    [calendarViewHeight]
  );

  const lastFetchedRangeRef = useRef<{ start: number; end: number } | null>(null);

  const handleCalendarDatesSet = useCallback(
    (arg: any) => {
      const view = arg?.view;
      const visibleStart: Date | undefined = view?.activeStart || arg?.start;
      const visibleEnd: Date | undefined = view?.activeEnd || arg?.end;

      if (!isValidDate(visibleStart) || !isValidDate(visibleEnd)) return;

      const startMs = visibleStart.getTime();
      const endMs = visibleEnd.getTime();
      const prev = lastFetchedRangeRef.current;
      // Skip no-op datesSet from FullCalendar option resets (e.g. opening post detail)
      if (prev && prev.start === startMs && prev.end === endMs) return;

      lastFetchedRangeRef.current = { start: startMs, end: endMs };
      if (!firstRangeSeenRef.current) firstRangeSeenRef.current = true;
      dispatch(
        AIContentAction.refreshCalendarData({
          start: visibleStart,
          end: visibleEnd,
        }) as any
      );
    },
    [dispatch]
  );

  const handleScheduleBatch = async () => {
    if (!projectId || !socialPosts?.length) {
      return;
    }

    const allowed = getAllowedPlatformsFromImmutable(aiSetting);
    const nonDraftPosts = socialPosts.filter((post: any) => {
      const status = String(post?.data?.status?.value || "").toUpperCase();
      return status === "DRAFT" && isPostOnAllowedPlatform(post, allowed);
    });

    if (nonDraftPosts.length === 0) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "warning",
          message: t("No posts available to schedule. All posts are drafts."),
        })
      );
      setShowApproveConfirm(false);
      return;
    }

    const groupedByScheduledTime = new Map<string, any[]>();
    nonDraftPosts.forEach((post: any) => {
      const scheduledTime = post?.data?.scheduledTime?.value || post?.scheduledTime?.value;
      if (scheduledTime) {
        const key = String(scheduledTime);
        if (!groupedByScheduledTime.has(key)) {
          groupedByScheduledTime.set(key, []);
        }
        groupedByScheduledTime.get(key)!.push(post);
      }
    });

    try {
      const promises = Array.from(groupedByScheduledTime.entries()).map(
        ([scheduledTime, posts]) => {
          const postIds = posts.map((post: any) => post.id);
          const scheduleBatchPayload = {
            postIds,
            publishTime: scheduledTime,
            requireApproval: true,
          };

          return dispatch(AIContentAction.scheduleBatch(projectId, scheduleBatchPayload) as any);
        }
      );

      const results = await Promise.all(promises);

      const hasError = results.some((result: any) => result?.payload?.error);
      if (hasError) {
        throw new Error("Some posts failed to schedule");
      }

      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "success",
          message: t("All posts scheduled successfully!"),
        })
      );

      setShowApproveConfirm(false);
      socialPostsQuery.refetch();
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || "Unknown error";

      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "error",
          message: t("Error scheduling posts: {{error}}", {
            error: errorMessage,
          }),
        })
      );
    }
  };

  const deletePostById = useCallback(
    async (postId: string) => {
      try {
        await deleteTask(postId);
        dispatch(
          LayoutAction.setNotification({
            title: t("Post"),
            type: "success",
            message: t("Deleted post"),
          })
        );
        socialPostsQuery.refetch();
      } catch (error: any) {
        dispatch(
          LayoutAction.setNotification({
            title: t("Post"),
            type: "error",
            message: t("Failed to delete post: {{error}}", {
              error: String(error?.message || error),
            }),
          })
        );
      }
    },
    [dispatch, socialPostsQuery, t]
  );

  const handleDeletePost = useCallback((postId: string | null) => {
    if (!postId) return;
    setPostIdToDelete(postId);
    setShowDeletePostConfirm(true);
  }, []);

  const handleConfirmDeletePost = useCallback(async () => {
    if (!postIdToDelete) {
      setShowDeletePostConfirm(false);
      return;
    }
    await deletePostById(postIdToDelete);
    setPostIdToDelete(null);
    setShowDeletePostConfirm(false);
  }, [deletePostById, postIdToDelete]);

  const handleUnpublishBatch = async () => {
    if (!projectId) return;

    try {
      const data = await apiUnscheduleAllSocialPosts(projectId!);
      if (data?.error) {
        throw new Error(data.error);
      }

      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "success",
          message: t("All scheduled posts were unscheduled successfully!"),
        })
      );

      setShowUnpublishConfirm(false);
      socialPostsQuery.refetch();
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || t("Unknown error");
      dispatch(
        LayoutAction.setNotification({
          title: t("Post"),
          type: "error",
          message: t("Error unpublishing posts: {{error}}", {
            error: errorMessage,
          }),
        })
      );
    }
  };

  const handleClearDraft = async () => {
    const allowed = getAllowedPlatformsFromImmutable(aiSetting);

    const drafts = (socialPosts || []).filter(
      (p: any) =>
        String(p?.data?.status?.value || "").toUpperCase() === "DRAFT" &&
        isPostOnAllowedPlatform(p, allowed)
    );

    const draftIds = drafts.map((d: any) => d.id).filter(Boolean);
    if (draftIds.length > 0) {
      await dispatch(
        TaskAction.bulkDeleteItems({
          itemIds: draftIds,
          type: "SALE",
        }) as any
      );
    }

    // Re-fetch from server to ensure consistent state instead of local mutation
    await socialPostsQuery.refetch();
    setShowClearDraftConfirm(false);
  };

  const renderEventContent = (arg: any) => {
    const ext = arg.event.extendedProps as any;
    return (
      <div
        onClick={() => {
          dispatch(AIContentAction.showPostDetailDialog(arg.event.id));
        }}
        className="cursor-pointer"
      >
        <PostCard
          title={arg.event.title}
          scheduledTimeUTC={ext?.scheduledTimeUTC}
          timezone={ext?.timezone?.value}
          status={ext?.status || "Unscheduled"}
          platform={(ext?.platform || "").toString()}
          postType={ext?.postType || "image"}
          onDelete={() => handleDeletePost(arg.event.id)}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-3 bg-white">



        {/* ── Full prompt header — only in calendar mode ── */}
        {viewMode === "calendar" && (
          <PromptHeader
            topic={topic}
            setTopic={setTopic}
            onGenerate={() => {
              if (!topic.trim()) {
                alert(t("Please enter a topic before generating content"));
                return;
              }
              setIsGenerateOpen(true);
            }}
            onApproveAll={() => {
              if (socialPosts.length === 0) return;
              setShowApproveConfirm(true);
            }}
            onUseSuggestedTopics={() => {
              dispatch(AIContentAction.deselectAllTopics() as any);
              dispatch(AIContentAction.openTopicsModal() as any);
            }}
            onClearPosts={() => {
              if (socialPosts.length === 0) return;
              setShowClearConfirm(true);
            }}
            onUnpublishAll={() => setShowUnpublishConfirm(true)}
            onClearDraft={() => setShowClearDraftConfirm(true)}
          />
        )}

        {viewMode === "calendar" && (
          <div className="flex flex-row pt-4">
            <div className="flex-1 mr-2">
              <CalendarToolbar
                calendarRef={calendarRef}
                eventsCount={socialPosts.length}
                onClearPosts={() => {
                  if (socialPosts.length === 0) return;
                  setShowClearConfirm(true);
                }}
                onToggleFullView={() => setIsFullCalendarView((v) => !v)}
                isFullCalendarView={isFullCalendarView}
                currentDate={currentDateFromRedux || new Date()}
                animateMonthLabel={false}
                viewMode={viewMode}
              />
            </div>
            {!isFullCalendarView && (
              <div className={`mt-2 lg:w-[287px] mr-6 flex-col hidden lg:block`}>
                <div className="flex items-center gap-1">
                  <span className="text-base font-bold text-gray-900">{t("Posts")}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-80px)]">
        {viewMode === "media" ? (
          /* ── MEDIA GRID VIEW ── */
          <div className="flex-1 px-4 lg:px-5 py-4">
            <MediaGrid
              projectId={projectId || ""}
              items={fileImages.toJS() as any[]}
              canvaItems={canvaDesigns.toJS() as any[]}
            />
          </div>
        ) : (
          /* ── CALENDAR VIEW ── */
          <>
            <div
              className={`flex-1 transition-all duration-300 ${isFullCalendarView ? "w-full" : "lg:w-2/3 xl:w-3/4"
                }`}
            >
              <div className="h-full px-4 lg:px-5">
                <CalendarView
                  socialPosts={socialPosts}
                  isGenerating={Boolean(
                    initialLoading ||
                    socialPostsQuery.isLoading ||
                    generatePostsMutation.isPending
                  )}
                  isFullCalendarView={isFullCalendarView}
                  onDateSelect={() => { }}
                  onEventDrop={() => { }}
                  onEventResize={() => { }}
                  onEventClick={(info) => {
                    dispatch(AIContentAction.showPostDetailDialog(info.event.id));
                  }}
                  renderEventContent={renderEventContent}
                  initialView={initialView}
                  calendarRef={calendarRef}
                  eventsCount={socialPosts.length}
                  onClearPosts={() => {
                    if (socialPosts.length === 0) return;
                    setShowClearConfirm(true);
                  }}
                  onToggleFullView={() => setIsFullCalendarView((v) => !v)}
                  onHeightChange={handleCalendarHeightChange}
                  onDatesSet={handleCalendarDatesSet}
                  monthAnimationKey={monthAnimationKey}
                />
              </div>
            </div>

            {!isFullCalendarView && (
              <div className="w-full lg:w-[287px] bg-white flex-shrink-0 mr-6 flex flex-col">
                <div className="flex-1 overflow-y-auto">
                  <PostsList
                    socialPosts={socialPosts}
                    isGenerating={Boolean(
                      initialLoading ||
                      socialPostsQuery.isLoading ||
                      generatePostsMutation.isPending
                    )}
                    onPostClick={(postData) => {
                      dispatch(AIContentAction.showPostDetailDialog(postData.id));
                    }}
                    calendarViewHeight={calendarViewHeight}
                    onDeletePost={(postId) => handleDeletePost(postId)}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <TopicsModal
        onConfirm={(selectedTopic) => {
          setTopic(selectedTopic.title);
          setIsGenerateOpen(true);
        }}
      />

      <PostDetailModal />
      <SharePostModal />

      <GenerateModal
        isOpen={isGenerateOpen}
        onClose={() => {
          setIsGenerateOpen(false);
        }}
        initialTopic={topic}
        onGenerate={({ dates, timezone, contentType, topic, includeWeekends, platforms }) => {
          const startDate = dates[0];
          const endDate = dates[dates.length - 1];

          // Use override=true when settings were changed so new AI settings apply to generated content
          const override = settingsChangedSinceLastGenerate;
          if (override) {
            dispatch(AIContentAction.clearAiSettingsChangedSinceLastGenerate() as any);
          }

          const generateParams = {
            projectId: projectId!,
            topic: topic || aiSetting?.get("topic") || "",
            prompt: null,
            startDate: format(startDate, "yyyy-MM-dd"),
            endDate: format(endDate, "yyyy-MM-dd"),
            includeWeekends: includeWeekends,
            totalDays: dates.length,
            language: aiSetting?.get("language") || "vi",
            postType: contentType as string,
            facebook: platforms.includes("facebook"),
            twitter: platforms.includes("twitter"),
            instagram: platforms.includes("instagram"),
            linkedin: platforms.includes("linkedin"),
            threads: platforms.includes("threads"),
            timezone: timezone,
            override,
            contentInstructions: resolveContentInstructions({
              contentInstructions: aiSetting?.get("contentInstructions"),
            }),
          };

          // Remember the selected start date to navigate calendar later
          try {
            lastGeneratedStartDateRef.current = startDate;
          } catch { }

          lastGenerateParamsRef.current = generateParams;
          triggerGenerateSocialPosts(generateParams);
          setIsGenerateOpen(false);
        }}
      />

      <ConfirmDialog
        visible={showClearConfirm}
        title={t("Clear Posts")}
        description={t("Clear all posts from calendar?")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={() => {
          dispatch(AIContentAction.clearSocialPosts() as any);
          setShowClearConfirm(false);
        }}
        onClose={() => setShowClearConfirm(false)}
      />

      <ConfirmDialog
        visible={showDeletePostConfirm}
        title={t("Delete post")}
        description={t(
          "Are you sure you would like to delete this post, this action is unable to undo?"
        )}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleConfirmDeletePost}
        onClose={() => {
          setShowDeletePostConfirm(false);
          setPostIdToDelete(null);
        }}
        dangerBtn={true}
      />

      <ConfirmDialog
        visible={showUnpublishConfirm}
        title={t("Unschedule All")}
        description={t("Are you sure you want to unschedule all posts?")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleUnpublishBatch}
        onClose={() => setShowUnpublishConfirm(false)}
      />

      <ConfirmDialog
        visible={showClearDraftConfirm}
        title={t("Clear Draft")}
        description={t("Are you sure you want to clear all drafts?")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleClearDraft}
        onClose={() => setShowClearDraftConfirm(false)}
      />

      <ConfirmDialog
        visible={showApproveConfirm}
        title={t("Approve All Posts")}
        description={t("Approve all posts for publishing?")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleScheduleBatch}
        onClose={() => setShowApproveConfirm(false)}
      />

      {/* Generate error confirm with retry */}
      <ConfirmDialog
        visible={showGenerateErrorConfirm}
        title={t("Generate Posts")}
        description={t("Error generating posts: {{error}}", {
          error: generateErrorMessage || t("Unknown error"),
        })}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={() => {
          if (lastGenerateParamsRef.current) {
            triggerGenerateSocialPosts(lastGenerateParamsRef.current);
          }
          setShowGenerateErrorConfirm(false);
        }}
        onClose={() => setShowGenerateErrorConfirm(false)}
      />
    </div>
  );
};

export default AIContentApp;
